import { Button } from '@material-ui/core';
import GlobalStyles from '@mui/material/GlobalStyles';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

import { WorkspaceAvatar } from 'src/components/WorkspaceAvatar';
import RowDivider from 'src/legacy/components/RowDivider';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import * as Colors from 'src/theme/colors';
import { getPublicImageURL } from 'src/utils/ImageUtils';

import { OnboardingFormCard } from '../OnboardingFormCard';
import { OnboardingFormCardTitle } from '../OnboardingFormCardTitle';

import { AuthenticatedPortalDataType } from './onboardingLoginTypes';

const WORKSPACE_CARD_WIDTH = 432;

const onboardingWorkspaceScrollbarGlobalStyles = {
  '::-webkit-scrollbar': {
    width: '6px',
    height: '6px',
  },
  '::-webkit-scrollbar-track': {
    WebkitBorderRadius: '10px',
    borderRadius: '10px',
  },
  '::-webkit-scrollbar-thumb': {
    WebkitBorderRadius: '10px',
    borderRadius: '10px',
    background: Colors.ScrollbarColor,
  },
};

const useStyles = makeStyles()((theme) => ({
  titleContainer: {
    textAlign: 'center',
    marginBottom: theme.spacing(5),
  },
  subTitleText: {
    color: Colors.GraySmall,
  },
  listWrapper: {
    [theme.breakpoints.up('sm')]: {
      marginTop: 102,
      marginBottom: 102,
      maxHeight: 'calc(100vh - 204px)',
      overflowY: 'auto',
      width: WORKSPACE_CARD_WIDTH,
    },
  },
  portalInfoContainer: {
    flex: 1,
    overflow: 'hidden',
    [theme.breakpoints.up('sm')]: {
      margin: theme.spacing(0, 3),
    },
    [theme.breakpoints.down('xs')]: {
      margin: theme.spacing(0, 2),
    },
  },
  portalInfoText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  linksButtons: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: theme.spacing(1),
    marginTop: theme.spacing(5.5),
  },
  workspaceContainer: {
    marginBottom: theme.spacing(4.5),
  },
}));

interface OnboardingWorkspaceSelectProps {
  authenticatedPortals: Record<
    string,
    Record<string, AuthenticatedPortalDataType>
  >;
  onWorkspaceSelected: (input: AuthenticatedPortalDataType) => void;
}

export const OnboardingWorkspaceSelect: React.FC<
  OnboardingWorkspaceSelectProps
> = ({ authenticatedPortals, onWorkspaceSelected }) => {
  const { classes } = useStyles();

  return (
    <>
      <GlobalStyles styles={onboardingWorkspaceScrollbarGlobalStyles} />
      <OnboardingFormCard className={classes.listWrapper}>
        <OnboardingFormCardTitle
          title=" These workspaces are linked to your account"
          subTitle="You can open them or create a new one"
        />
        {Object.entries(authenticatedPortals).map(([email, portals]) => (
          <div className={classes.workspaceContainer} key={email}>
            <BaseTypography
              fontType="13Medium"
              className={`${classes.portalInfoText} ml-2`}
            >
              {email}
            </BaseTypography>
            <RowDivider mt={1.5} mb={1.5} />
            {Object.entries(portals).map(([portalId, data]) => (
              <React.Fragment key={portalId}>
                <div className="flex items-center w-full p-2">
                  <WorkspaceAvatar
                    iconSrc={getPublicImageURL(data.iconUrl)}
                    workspaceName={data.name}
                    size={34}
                  />
                  <div className={classes.portalInfoContainer}>
                    <BaseTypography
                      fontType="15Medium"
                      className={classes.portalInfoText}
                    >
                      {data.name}
                    </BaseTypography>
                  </div>
                  <Button
                    color="secondary"
                    variant="contained"
                    onClick={() => onWorkspaceSelected(data)}
                  >
                    Open
                  </Button>
                </div>
                <RowDivider mt={1.5} mb={1.5} />
              </React.Fragment>
            ))}
          </div>
        ))}

        <div className={classes.linksButtons}>
          <Button href="/add-account">
            <BaseTypography className={classes.subTitleText}>
              Log in to another account
            </BaseTypography>
          </Button>

          <Button href="/workspaces">
            <BaseTypography className={classes.subTitleText}>
              Create a new workspace
            </BaseTypography>
          </Button>
        </div>
      </OnboardingFormCard>
    </>
  );
};
